
/**
 * Module dependencies.
 */

import { AmbientProps } from 'src/types/ambients';
import { kioskRoutes } from 'src/core/routes';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import { useRouter } from 'next/router';
import Image, { ImageProps } from 'src/components/core/image';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import get from 'lodash/get';
import head from 'lodash/head';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Pick<AmbientProps, 'id' | 'media' | 'title'> & {
  className?: string,
  isKioskCard?: boolean
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  transition: transform 5s ease-in;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)`
  display: block;
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  &:focus,
  &:hover {
    ${StyledImage} {
      transform: scale(1.5);
    }
  }
`;

/**
 * `AmbientCard` component.
 */

const AmbientCard = (props: Props): ReactElement | null => {
  const { className, id, isKioskCard, media, title } = props;
  const { locale } = useRouter();
  const imageUrl = get(head(media), 'fileUrl');
  const alt = get(head(media), 'customProperties.title');
  const routeResolver = useLocalizedRoute();
  const kioskRoute = routeTemplate(kioskRoutes.inspirationDetails, { id });
  const route = routeResolver('inspirationDetails', { id });

  if (!imageUrl) {
    return null;
  }

  return (
    <Card
      className={className}
      href={isKioskCard ? kioskRoute : route}
      locale={locale}
    >
      <StyledImage
        alt={alt ? alt : title}
        layout={'fill'}
        objectFit={'cover'}
        src={imageUrl}
      />
    </Card>
  );
};

/**
 * Export `AmbientCard` component.
 */

export default AmbientCard;
